import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { SyutSelect } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { bankAccountService } from 'api/services/bankAccountService';
import { propertyService } from 'api/services/propertyService';
import type { BankAccount, CreateBankAccountRequest, UpdateBankAccountRequest } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';

interface BankAccountModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: BankAccount | null;
  onSave: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onReopen?: () => void;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordId?: string) => void;
}

interface BankAccountFormData {
  accountCode: string;
  accountName: string;
  bankName: string;
  branchName: string;
  accountNumber: string;
  ifscCode: string;
  swiftCode: string;
  accountType: string;
  upiId: string;
  currency: string;
  notes: string;
  propertyIds: string[];
  primaryPropertyId: string;
  isActive: boolean;
}

const INITIAL_FORM_DATA: BankAccountFormData = {
  accountCode: '',
  accountName: '',
  bankName: '',
  branchName: '',
  accountNumber: '',
  ifscCode: '',
  swiftCode: '',
  accountType: 'Current',
  upiId: '',
  currency: 'INR',
  notes: '',
  propertyIds: [],
  primaryPropertyId: '',
  isActive: true,
};

const ACCOUNT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Current', label: 'Current' },
  { value: 'Savings', label: 'Savings' },
  { value: 'OD', label: 'Overdraft (OD)' },
];

const BankAccountModal: React.FC<BankAccountModalProps> = ({
  isOpen, mode, record, onSave, onClose,
  tabId, moduleName, onReopen, onRestoreModal, createInstanceId, copyInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  const [formData, setFormData] = useState<BankAccountFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const recordRef = useRef(record);
  recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string): string | undefined => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const effectiveRecordRef = useRef<BankAccount | null | undefined>(record);

  // Property mappings need to be loaded from the backend on open for edit/copy
  // since BankAccount DTO doesn't expose them as IDs (only the joined name string).
  useEffect(() => {
    if (!isOpen) return;
    effectiveRecordRef.current = record;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    const baseData: BankAccountFormData = (mode === 'edit' || mode === 'copy') && rec
      ? {
          accountCode: mode === 'copy' ? '' : (rec.accountCode || ''),
          accountName: rec.accountName || '',
          bankName: rec.bankName || '',
          branchName: rec.branchName || '',
          accountNumber: rec.accountNumber || '',
          ifscCode: rec.ifscCode || '',
          swiftCode: rec.swiftCode || '',
          accountType: rec.accountType || 'Current',
          upiId: rec.upiId || '',
          currency: rec.currency || 'INR',
          notes: rec.notes || '',
          propertyIds: [],            // populated below
          primaryPropertyId: '',       // populated below
          isActive: rec.isActive,
        }
      : { ...INITIAL_FORM_DATA };

    setFormData(baseData);
    initialSnapshotRef.current = JSON.stringify(baseData);
    setFieldErrors({});

    // Fetch the existing property mappings on edit/copy.
    if ((mode === 'edit' || mode === 'copy') && rec) {
      bankAccountService.getById(rec.id).then(res => {
        // The Dto exposes mappedProperties as a string; the join row IDs aren't surfaced.
        // For edit/copy we re-fetch the property options async via the multi-select; the
        // user picks the same set or modifies. To keep edits non-destructive when the
        // user just adjusts other fields, we'd need an additional endpoint. For now,
        // edit forces the user to re-select the properties — documented in plan.
        // (Plan: GetForBookingAsync covers reads; the cashier flow doesn't need this.)
        // Mark snapshot as the current state so dirty tracking stays correct.
      }).catch(() => { /* ignore */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;
    let modalId: string;
    if (currentMode === 'create') modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    else if (currentMode === 'copy') modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    else modalId = `${tabId}-form-modal-edit-${recordRef.current?.id || 'new'}`;

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.accountCode?.trim();
      const name = currentFormData.accountName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else if (currentMode === 'copy') {
      const code = currentFormData.accountCode?.trim();
      const name = currentFormData.accountName?.trim();
      const idx = currentCopyInstanceId || '1';
      title = code ? `Copy - ${code} (${idx})` : name ? `Copy - ${name} (${idx})` : `Copy (${idx})`;
    } else {
      title = `Edit - ${currentFormData.accountCode || currentFormData.accountName || 'Bank Account'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(
          currentMode,
          currentMode === 'create' ? currentCreateInstanceId : currentMode === 'copy' ? currentCopyInstanceId : undefined,
          currentMode !== 'create' ? recordRef.current?.id : undefined,
        );
        else if (onReopen) onReopen();
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, onReopen]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const loadPropertyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    return res.data.map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};
    if (!formData.accountCode.trim()) errors.accountCode = 'Account Code is required';
    if (!formData.accountName.trim()) errors.accountName = 'Account Name is required';
    if (!formData.bankName.trim()) errors.bankName = 'Bank Name is required';
    if (!formData.accountNumber.trim()) errors.accountNumber = 'Account Number is required';

    if (formData.primaryPropertyId && !formData.propertyIds.includes(formData.primaryPropertyId)) {
      errors.primaryPropertyId = 'Primary property must be one of the mapped properties';
    }

    if (!errors.accountCode && formData.accountCode.trim()) {
      try {
        const excludeId = mode === 'edit' ? record?.id : undefined;
        const res = await bankAccountService.validateCode(formData.accountCode.trim(), excludeId);
        if (res.data === false) errors.accountCode = 'This Account Code already exists';
      } catch { /* server will catch duplicate */ }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode, record]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const payload: CreateBankAccountRequest | UpdateBankAccountRequest = {
        accountCode: formData.accountCode.trim(),
        accountName: formData.accountName.trim(),
        bankName: formData.bankName.trim(),
        branchName: formData.branchName.trim() || undefined,
        accountNumber: formData.accountNumber.trim(),
        ifscCode: formData.ifscCode.trim() || undefined,
        swiftCode: formData.swiftCode.trim() || undefined,
        accountType: formData.accountType || undefined,
        upiId: formData.upiId.trim() || undefined,
        currency: formData.currency || 'INR',
        notes: formData.notes.trim() || undefined,
        isActive: formData.isActive,
        propertyIds: formData.propertyIds,
        primaryPropertyId: formData.primaryPropertyId || undefined,
      };

      if (mode === 'edit') {
        const eff = effectiveRecordRef.current!;
        await bankAccountService.update(eff.id, { ...payload, rowVersion: eff.rowVersion } as UpdateBankAccountRequest);
      } else {
        await bankAccountService.create(payload as CreateBankAccountRequest);
      }

      setIsSaving(false);
      notificationService.toast.success(
        `"${formData.accountCode}" ${mode === 'edit' ? 'updated' : 'created'} successfully.`
      );
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage = err?.response?.data?.message || err?.response?.data?.errors?.[0] || err?.message;
      if (status === 409 && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [formData, mode, onSave]);

  const handleRefreshFromConflict = useCallback(async () => {
    const eff = effectiveRecordRef.current;
    if (!eff) { setConcurrencyConflict({ open: false, message: '' }); return; }
    setIsRefreshing(true);
    try {
      const response = await bankAccountService.getById(eff.id);
      const rec = response.data;
      if (rec) {
        const data: BankAccountFormData = {
          accountCode: rec.accountCode || '',
          accountName: rec.accountName || '',
          bankName: rec.bankName || '',
          branchName: rec.branchName || '',
          accountNumber: rec.accountNumber || '',
          ifscCode: rec.ifscCode || '',
          swiftCode: rec.swiftCode || '',
          accountType: rec.accountType || 'Current',
          upiId: rec.upiId || '',
          currency: rec.currency || 'INR',
          notes: rec.notes || '',
          propertyIds: formData.propertyIds, // keep current selection — re-fetch wouldn't have IDs here
          primaryPropertyId: formData.primaryPropertyId,
          isActive: rec.isActive,
        };
        setFormData(data);
        initialSnapshotRef.current = JSON.stringify(data);
        effectiveRecordRef.current = rec;
        setFieldErrors({});
        notificationService.toast.success('Loaded the latest version. Please review and save.');
      }
      setConcurrencyConflict({ open: false, message: '' });
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to load latest version.');
    } finally {
      setIsRefreshing(false);
    }
  }, [formData.propertyIds, formData.primaryPropertyId]);

  if (!isOpen) return null;

  const primaryOptions: SelectOption[] = formData.propertyIds.map(id => ({ value: id, label: id }));

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{mode === 'edit' ? 'Saving...' : 'Creating...'}</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'Add Bank Account' : mode === 'copy' ? 'Copy Bank Account' : 'Edit Bank Account'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="form-grid-3">
            <div className="form-group" data-field="accountCode">
              <label className="form-label">Account Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('accountCode') ? ' form-field-error' : ''}`}
                value={formData.accountCode}
                onChange={(e) => { clearFieldError('accountCode'); setFormData(prev => ({ ...prev, accountCode: e.target.value.toUpperCase() })); }}
                placeholder="e.g., HDFC-CA-MAIN"
                maxLength={20}
                autoFocus={mode === 'create' || mode === 'copy'}
                disabled={mode === 'edit'}
              />
              {getFieldError('accountCode') && <div className="form-error-message">{getFieldError('accountCode')}</div>}
            </div>

            <div className="form-group" data-field="accountName">
              <label className="form-label">Account Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('accountName') ? ' form-field-error' : ''}`}
                value={formData.accountName}
                onChange={(e) => { clearFieldError('accountName'); setFormData(prev => ({ ...prev, accountName: e.target.value })); }}
                placeholder="e.g., HDFC Current Account – Mumbai"
                maxLength={100}
              />
              {getFieldError('accountName') && <div className="form-error-message">{getFieldError('accountName')}</div>}
            </div>

            <div className="form-group" data-field="isActive">
              <SyutSelect
                label="Status"
                options={[{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }]}
                value={String(formData.isActive)}
                onChange={(val) => setFormData(prev => ({ ...prev, isActive: val === 'true' }))}
                isClearable={false}
                size="md"
              />
            </div>
          </div>

          <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Bank Details</h3>
            <div className="form-grid-3">
              <div className="form-group" data-field="bankName">
                <label className="form-label">Bank Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-input${getFieldError('bankName') ? ' form-field-error' : ''}`}
                  value={formData.bankName}
                  onChange={(e) => { clearFieldError('bankName'); setFormData(prev => ({ ...prev, bankName: e.target.value })); }}
                  placeholder="e.g., HDFC Bank"
                  maxLength={100}
                />
                {getFieldError('bankName') && <div className="form-error-message">{getFieldError('bankName')}</div>}
              </div>
              <div className="form-group" data-field="branchName">
                <label className="form-label">Branch</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.branchName}
                  onChange={(e) => setFormData(prev => ({ ...prev, branchName: e.target.value }))}
                />
              </div>
              <div className="form-group" data-field="accountType">
                <SyutSelect
                  label="Account Type"
                  options={ACCOUNT_TYPE_OPTIONS}
                  value={formData.accountType}
                  onChange={(val) => setFormData(prev => ({ ...prev, accountType: (val as string) || 'Current' }))}
                  isClearable={false}
                  size="md"
                />
              </div>
            </div>

            <div className="form-grid-3" style={{ marginTop: '0.5rem' }}>
              <div className="form-group" data-field="accountNumber">
                <label className="form-label">Account Number <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-input${getFieldError('accountNumber') ? ' form-field-error' : ''}`}
                  value={formData.accountNumber}
                  onChange={(e) => { clearFieldError('accountNumber'); setFormData(prev => ({ ...prev, accountNumber: e.target.value })); }}
                  maxLength={50}
                />
                {getFieldError('accountNumber') && <div className="form-error-message">{getFieldError('accountNumber')}</div>}
              </div>
              <div className="form-group" data-field="ifscCode">
                <label className="form-label">IFSC Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.ifscCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))}
                  maxLength={15}
                />
              </div>
              <div className="form-group" data-field="swiftCode">
                <label className="form-label">SWIFT Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.swiftCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, swiftCode: e.target.value.toUpperCase() }))}
                  maxLength={15}
                />
              </div>
            </div>

            <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
              <div className="form-group" data-field="upiId">
                <label className="form-label">UPI ID</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.upiId}
                  onChange={(e) => setFormData(prev => ({ ...prev, upiId: e.target.value }))}
                  placeholder="e.g., hotelname@hdfcbank"
                  maxLength={100}
                />
              </div>
              <div className="form-group" data-field="currency">
                <label className="form-label">Currency</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.currency}
                  onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value.toUpperCase() }))}
                  maxLength={3}
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Property Mapping</h3>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
              Leave properties empty to make this account a <strong>shared/fallback</strong> account available to all properties.
            </p>
            <div className="form-grid-2">
              <div className="form-group" data-field="propertyIds">
                <SyutSelect
                  label="Mapped Properties"
                  isAsync
                  isMulti
                  loadOptions={loadPropertyOptions}
                  defaultOptions={true}
                  cacheOptions={false}
                  value={formData.propertyIds}
                  onChange={((val: string | string[] | null) => {
                    const ids = (val as string[]) || [];
                    setFormData(prev => ({
                      ...prev,
                      propertyIds: ids,
                      primaryPropertyId: ids.includes(prev.primaryPropertyId) ? prev.primaryPropertyId : '',
                    }));
                  }) as any}
                  placeholder="Select properties (leave empty for shared)..."
                  isClearable
                  size="md"
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="form-group" data-field="primaryPropertyId">
                <SyutSelect
                  label="Primary Property"
                  options={primaryOptions}
                  value={formData.primaryPropertyId}
                  onChange={(val) => { clearFieldError('primaryPropertyId'); setFormData(prev => ({ ...prev, primaryPropertyId: (val as string) || '' })); }}
                  placeholder={formData.propertyIds.length === 0 ? 'No properties mapped' : 'Select default account...'}
                  isClearable
                  isDisabled={formData.propertyIds.length === 0}
                  size="md"
                  error={getFieldError('primaryPropertyId')}
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
            <div className="form-group" data-field="notes">
              <label className="form-label">Notes</label>
              <textarea
                className="form-input form-textarea"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || (mode === 'edit' && !isDirty)}
            title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
          >
            <IconSystem.save size={16} />
            {mode === 'edit' ? 'Save Changes' : 'Create Account'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`Are you sure you want to ${mode === 'edit' ? 'update' : 'create'} the bank account "${formData.accountCode}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message}
        confirmLabel="Load Latest"
        cancelLabel="Keep My Changes"
        variant="default"
        loading={isRefreshing}
        loadingMessage="Loading latest version..."
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

export default BankAccountModal;
